<template>
<div>
    <div v-if="sendStatus == 'Sending'">
        <h2>
            {{ $t("invitePreviousSponsorsView.sendingInvitations") }}
        </h2>
        <kk-spinner />
    </div>
    <div v-else-if="sendStatus == 'Sent'">
        <h2>
            {{ $t("invitePreviousSponsorsView.invitationsSent") }}
        </h2>
    </div>
    <div v-else>
        <div v-if="sponsors.length == 0" class="section">
            {{ $t("invitePreviousSponsorsView.noSponsors") }}
        </div>
        <div v-else>
            <p>{{ $t("invitePreviousSponsorsView.invitingSponsorsTo", [store.selectedOrganization.name, store.selectedEvent.name]) }}</p>
            <h2>{{ $t("invitePreviousSponsorsView.chooseSponsors") }}</h2>
            <div class="invite-previous-sponsors-button">
                <button class="button button-green invite-previous-sponsors-button" @click="sendInvitations" :disabled="selectedSponsorIds.length == 0">
                    {{ $t("invitePreviousSponsorsView.inviteSelected") }}
                </button>
            </div>

            <div class="list-item">
                <div class="left-part">
                    <input type="checkbox" @click="toggleAll" :checked="allSelected" />
                </div>
                <div class="right-part">
                    <h3>{{ $t("invitePreviousSponsorsView.selectAll") }}</h3>
                </div>
            </div>
            <div class="previous-sponsor-list">
                <div v-for="orgId in Object.keys(sponsorsByOrg)" :key="orgId">
                    <div class="previous-sponsor-list-organization-title">
                        {{ $t("invitePreviousSponsorsView.sponsorsFrom", [ sponsorsByOrg[orgId][0].organizationName ]) }}
                    </div>
                    <div class="list-item" v-for="s in sponsorsByOrg[orgId]" :key="s.sponsorId">
                        <div class="left-part">
                            <input v-if="s.sponsor.email" :id="s.sponsor.sponsorId" :value="s.sponsor.sponsorId" type="checkbox" v-model="selectedSponsorIds" />
                            <span v-if="!s.sponsor.email" class="icon icon-question" @click="infoPopup()"></span>
                        </div>
                        <div class="right-part">
                            <div v-if="s.sponsor.sponsorType === 'Private'">{{ s.sponsor.fullName }}</div>
                            <div v-else>{{ s.sponsor.companyName }}</div>
                            <div v-if="s.sponsor.email && !s.sponsor.editingEmail">{{ s.sponsor.email }}</div>
                            <input v-else @keydown="emailInput(s.sponsor, $event)" type="text" v-model="s.sponsor.email" :placeholder="$t('invitePreviousSponsorsView.setEmail')" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <kk-generic-popup :title="$t('invitePreviousSponsorsView.infoPopupTitle')" :visible="popupVisible" @close="closePopup()">
        <p>{{ $t("invitePreviousSponsorsView.infoPopupText") }}</p>
        <button type="button" class="button" @click="goToAddSponsor()">{{ $t("sponsorsView.addSponsor") }} <span class="icon-arrow-right2"></span></button>
        <button type="button" class="button" @click="goToContacts()">{{ $t("invitePreviousSponsorsView.goToContacts")}} <span class="icon-arrow-right2"></span></button>
    </kk-generic-popup>
</div>
</template>

<script lang="ts">
import { store, Sponsor } from "store";
import "@/components/KkGenericPopup.vue";
import axios from "axios";
import xamatron from "xamatron";
import { defineComponent } from "vue";

type SendStatus = "Sending" | "Sent";

interface PreviousSponsor {
    sponsor: Sponsor;
    organizationName: string;
    organizationId: number;
}

interface SetupData {
    sponsors: PreviousSponsor[];
    sponsorsByOrg: { [organizationId: number]: PreviousSponsor[] };
}

export default defineComponent({
    async setup(): SetupData {
        let previousSponsors = (await axios.get<PreviousSponsor[]>("sponsors/previous-sponsors")).data;

        // Don't include sponsors that are already on user's sponsor list
        const sponsors = previousSponsors
            .filter(s =>
                !store.selectedEvent.sponsors.some(sp => sp.sponsorId === s.sponsor.sponsorId || sp.email === s.sponsor.email));

        const sponsorsByOrg = {};

        // Group sponsors by organizationId
        for (let sponsor of sponsors) {
            if (sponsorsByOrg[sponsor.organizationId]) {
                sponsorsByOrg[sponsor.organizationId].push(sponsor);
            } else {
                sponsorsByOrg[sponsor.organizationId] = [sponsor];
            }
        }

        return {
            sponsors,
            sponsorsByOrg,
        };
    },
    data() {
        const selectedSponsorIds: number[] = [];
        const sendStatus: SendStatus = null;

        return {
            store: store,
            error: null,
            popupVisible: false,
            sendStatus,
            selectedSponsorIds,
            numSponsors: 0
        };
    },
    computed: {
        allSelected(): boolean {
            return this.selectedSponsorIds.length === this.sponsors.length;
        }
    },
    methods: {
        toggleAll() {
            let all = this.allSelected;
            this.selectedSponsorIds.splice(0);
            if (!all) {
                for (let s in this.sponsorsByOrg) {
                    this.sponsorsByOrg[s].forEach(s => this.selectedSponsorIds.push(s.sponsor.sponsorId));
                }
            }
        },
        emailInput(sponsor: Sponsor, e) {
            sponsor.editingEmail = true;
        },
        async sendInvitations() {
            this.sendStatus = "Sending";
            let selectedSponsors = this.sponsors
                .filter(s => s.sponsor.email && this.selectedSponsorIds.some(id => id === s.sponsor.sponsorId))
                .map(s => s.sponsor);

            try {
                let emails = selectedSponsors.map(s => s.email.trim());
                await axios.post("sponsors/invite-email", { eventId: this.store.selectedEvent.eventId, emails });
                this.$set(store.selectedEvent, "sponsors", store.selectedEvent.sponsors.concat(selectedSponsors));
                this.sendStatus = "Sent"
            } catch(e) {
                this.error = e.message;
            }
        },
        infoPopup() {
            this.popupVisible = true;
        },
        closePopup() {
            this.popupVisible = false;
        },
        goToAddSponsor() {
            this.$router.push("/add-sponsor");
        },
        goToContacts() {
            xamatron.openURL(this.$getSiteUrl("public/fororganizations"));
        }
    }
})

</script>